import { LocalFind, LocalInsert, LocalRemove } from '@weblite/persist'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

export async function getNotificationPermissionState(): Promise<
  NotificationPermission | PermissionState
> {
  // The value returned by navigator.permissions.query is wrong in `iOS-Safari` (returns `prompt` when it is `granted`)
  if (window.Notification?.permission) {
    return Promise.resolve(window.Notification.permission)
  }

  if ('permissions' in navigator && navigator.permissions.query) {
    return navigator.permissions
      .query({
        name: 'notifications',
      })
      .then(permission => {
        return permission.state
      })
  }
  return Promise.reject(new Error('Can not get notification permission!'))
}

export function subscribeToNotificationPermissionChange(
  cb: (value: NotificationPermission | PermissionState) => unknown,
) {
  window.navigator?.permissions
    ?.query?.({ name: 'notifications' })
    .then(function (notificationPerm) {
      notificationPerm.onchange = function () {
        if (window.Notification?.permission) {
          cb(window.Notification?.permission)
        } else {
          cb(notificationPerm.state)
        }
      }
    })
}

const setPermissionInWindow = (permission: NotificationPermission) => {
  if (!('permission' in Notification)) {
    // @ts-ignore
    Notification.permission = permission
  }
}

export async function requestNotificationPermission() {
  try {
    return Notification.requestPermission().then(permission => {
      setPermissionInWindow(permission)

      return permission === 'granted'
        ? permission
        : Promise.reject(new Error('Unable to get permission to notify.'))
    })
  } catch (e) {
    // Note: Safari still uses the callback syntax to get the permission.
    // https://developer.mozilla.org/en-US/docs/Web/API/Notifications_API/Using_the_Notifications_API#example
    return new Promise((resolve, reject) =>
      Notification.requestPermission(permission => {
        setPermissionInWindow(permission)

        if (permission === 'granted') resolve(permission)
        else reject(new Error('Unable to get permission to notify.'))
      }),
    )
  }
}

const LAST_NOTIF_PERMISSION_REJECTION_TIME_KEY =
  'lastNotifPermissionRejectionTime'
export const setLastNotifPermissionRejectionTime = () =>
  LocalInsert(LAST_NOTIF_PERMISSION_REJECTION_TIME_KEY, dateWithOffset())

export const getLastNotifPermissionRejectionTime = () =>
  LocalFind(LAST_NOTIF_PERMISSION_REJECTION_TIME_KEY) as number | undefined

export const removeLastNotifPermissionRejectionTime = () =>
  LocalRemove(LAST_NOTIF_PERMISSION_REJECTION_TIME_KEY)
